import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ChatInput from './ChatInput';
import ChatMessages from './ChatMessages';
import { useChat } from '../../context/ChatContext';
import { useSocket } from '../../context/SocketContext';
import Loader from '../common/Loader';
import DeleteChatModal from '../ui/ChatDeleteModal';
import OfflineToast from '../ui/OfflineToast';
import { ArrowDown } from 'lucide-react';

export default function Chat() {
  const { chatId } = useParams();
  const location = useLocation();
  const { trimmedMessage } = location?.state || {};
  const navigate = useNavigate();
  const {
    setActiveChatId,
    getChatsById,
    renameChatById,
    getChatHistory,
    deleteChatId,
  } = useChat();
  const { messages: liveMessages, sendMessage, isRespLoading } = useSocket();
  const scrollableDivRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  // State to track offline/online status
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isOnline, setIsOnline] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Handle the online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false); // User is online
      setIsOnline(true);

      // Auto-hide after 3 seconds
      setTimeout(() => {
        setIsOnline(false);
      }, 3000);
    };
    const handleOffline = () => {
      setIsOffline(true); // User is offline
      setIsOnline(false);
    };

    // Add event listeners for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup the event listeners when the component unmounts
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Scroll Into View when new message arrive
  useEffect(() => {
    if (showScrollToBottom || !scrollableDivRef.current) return;
    const timeout = setTimeout(() => {
      scrollableDivRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
    return () => clearTimeout(timeout);
  }, [history, liveMessages, chatId, showScrollToBottom]);

  useEffect(() => {
    if (!chatId) return;
    setActiveChatId(chatId);
    setLoading(true);
    setInput('');

    const fetchChatHistory = async () => {
      try {
        const chat = await getChatsById(chatId);
        setHistory(chat.history);
      } catch (error) {
        console.error('Failed to fetch chat history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChatHistory();
  }, [chatId]);

  // Clear history when switching to new chat (no chatId)
  useEffect(() => {
    if (!chatId) {
      setHistory([]);
      setInput('');
    }
  }, [chatId]);


  useEffect(() => {
    if (!trimmedMessage) return;

    sendMessage(trimmedMessage);
    handleAutoRenameFunc(trimmedMessage);
    setInput('');

    // Clear trimmedMessage from location.state
    navigate(location.pathname, { replace: true, state: {} });
  }, [trimmedMessage]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    sendMessage(trimmed);
    setInput('');
    //immediately scroll
    scrollToBottom();
  };
  // Combine messages safely - filter out any duplicate messages
  // This prevents old WebSocket messages from appearing in history chats
  const combinedMessages = (() => {
    if (!chatId) return [...history];
    
    // Filter out duplicate messages between history and liveMessages
    const allMessages = [...history, ...liveMessages];
    const uniqueMessages = allMessages.filter((message, index, array) => {
      // Find the first occurrence of this message
      const firstIndex = array.findIndex(msg => 
        msg.message === message.message && msg.role === message.role
      );
      // Only keep the first occurrence
      return firstIndex === index;
    });
    
    return uniqueMessages;
  })();

  const handleAutoRenameFunc = (value) => {
    setInput(value);

    setTimeout(async () => {
      const trimmed = value.trim();
      if (trimmed.length > 0 && chatId) {
        try {
          await renameChatById(
            chatId,
            trimmed.length > 50 ? trimmed.slice(0, 50) : trimmed
          );

          await getChatHistory();
        } catch (error) {
          console.error('Auto-Rename failed:', error);
        }
      }
    }, 1000);
  };

  // Show/hide scroll to bottom arrow based on scroll position
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= 100;
    setShowScrollToBottom(!isNearBottom);
  };

  const scrollToBottom = () => {
    scrollableDivRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Re-enable auto-scroll
    setShowScrollToBottom(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleScroll();
    }, 100);
    return () => clearTimeout(timeout);
  }, [combinedMessages.length]);

  return (
    <>
      <div
        className="h-full overflow-y-auto"
        ref={chatContainerRef}
        onScroll={handleScroll}
      >
        <div className="flex flex-col h-full max-w-3xl mx-auto">
          <>
            {loading ? (
              <div className="w-full h-full">
                <Loader width="60px" />
              </div>
            ) : (
              <ChatMessages
                messages={combinedMessages}
                ref={showScrollToBottom ? null : scrollableDivRef}
                isRespLoading={isRespLoading}
                chatId={chatId}
              />
            )}
            <ChatInput
              message={input}
              setMessage={setInput}
              handleSend={handleSend}
            />
            {/* Scroll to Bottom Arrow */}
            {showScrollToBottom && (
              <div className="fixed flex justify-center w-full max-w-3xl bottom-32">
                <button
                  className="p-2 text-white bg-gray-800 rounded-full shadow-lg hover:bg-gray-700"
                  onClick={scrollToBottom}
                >
                  <ArrowDown size={24} />
                </button>
              </div>
            )}
          </>
        </div>
      </div>
      {deleteChatId && <DeleteChatModal />}

      {/* Show Offline Toast when user is offline */}
      {isOnline ? (
        <OfflineToast
          message="You are back online."
          visible={isOnline}
          type="online"
        />
      ) : (
        <OfflineToast
          message="You are offline. Please check your internet connection."
          visible={isOffline}
          type="offline"
        />
      )}
    </>
  );
}
